import React, {useEffect, useMemo} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import Header from "../../ThemeComponents/Header";
import Footer from "../../ThemeComponents/Footer";
import Devider from "../../ThemeComponents/Devider";
import {getAppliedWhiteLabelThemeId} from "../../config/whiteLabelThemes";
import {forceScrollToTop} from "../../config/scrollToTop";
import {sendEvent} from "../../config/analytics";

const APP_QR_URL = "https://kav2go.nl/assets/kavappqr.png";
const IOS_APP_URL = "https://apps.apple.com/nl/app/kav2go-bestelbusverhuur/id1089515042";
const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=nl.deft.kav.kav";
const PRIVATESHARE_ANDROID_APP_URL = "https://play.google.com/apps/internaltest/4701218880367765331";
const PRIVATESHARE_ANDROID_APP_QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=8&data=${encodeURIComponent(PRIVATESHARE_ANDROID_APP_URL)}`;

const detectClientDevice = () => {
    if (typeof window === "undefined") {
        return {isMobile: false, platform: "desktop"};
    }

    const userAgent = window.navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);

    if (isIOS) return {isMobile: true, platform: "ios"};
    if (isAndroid) return {isMobile: true, platform: "android"};
    if (isMobile) return {isMobile: true, platform: "mobile_other"};
    return {isMobile: false, platform: "desktop"};
};

const formatDateTime = (value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const getReservationIdentifier = (reservation) => {
    if (!reservation) return "";
    return (
        reservation?.reservation_number ||
        reservation?.reservation_request_number ||
        reservation?.reservation_request_id ||
        reservation?.request_number ||
        reservation?.request_id ||
        reservation?.reservationNumber ||
        reservation?.number ||
        reservation?.id ||
        ""
    );
};

const ReservationResultPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(location.search);
    const reservationFromState = location.state?.reservation || null;

    const reservationNumber = queryParams.get("reservationId")
        || String(getReservationIdentifier(reservationFromState) || "--");
    const reservationFrom = queryParams.get("from")
        || reservationFromState?.planned_start_date
        || "";
    const reservationTo = queryParams.get("to")
        || reservationFromState?.planned_end_date
        || "";
    const vehiclePlate = queryParams.get("plate")
        || reservationFromState?.vehicle?.license_plate
        || reservationFromState?.license_plate
        || "--";
    const vehicleName = queryParams.get("vehicleName")
        || location.state?.vehicleInfo?.name
        || reservationFromState?.vehicle?.name
        || "";
    const rentalUnit = String(
        queryParams.get("rentalUnit")
        || location.state?.vehicleInfo?.rentalUnit
        || location.state?.vehicleInfo?.rental_config?.minimum_rental_time?.unit
        || reservationFromState?.vehicle?.rental_config?.minimum_rental_time?.unit
        || ""
    ).toLowerCase();
    const resultType = queryParams.get("resultType") || location.state?.resultType || "reservation";
    const isP2PRequest = resultType === "p2p_request";
    const isPrivateShareTheme = getAppliedWhiteLabelThemeId() === "privateshare";
    const isPrivateShareP2PRequest = isPrivateShareTheme && isP2PRequest;
    const shouldShowAppDownload = rentalUnit === "hour" && !isP2PRequest;

    const clientDevice = useMemo(() => detectClientDevice(), []);

    useEffect(() => {
        forceScrollToTop();
    }, [location.pathname]);

    useEffect(() => {
        sendEvent("reserve_result_view");
    }, []);

    return (
        <>
            <Header/>
            <Devider />

            <div className="container mt-5 mb-5">
                <div className="kav-result">
                    <div className="kav-result__icon">
                        <i className="bi bi-check2-circle"></i>
                    </div>
                    <h1 className="kav-result__title">
                        {isP2PRequest ? "Je aanvraag is verstuurd" : "Je reservering is aangemaakt"}
                    </h1>
                    {isPrivateShareP2PRequest ? (
                        <div className="kav-result__intro">
                            <p>
                                De eigenaar van het voertuig heeft je verzoek ontvangen. De reservering is definitief als de aanvraag wordt bevestigd.
                            </p>
                            <p>Informatie over de aanvraag vindt u hieronder.</p>
                        </div>
                    ) : (
                        <p className="kav-result__subtitle">
                            {isP2PRequest
                                ? "De eigenaar ontvangt je aanvraag en kan deze bevestigen. Hieronder vindt u informatie over uw aanvraag."
                                : "Reservering voltooid. Hieronder vindt u informatie over uw boeking. Een kopie van de boeking is naar uw e-mail verzonden."}
                        </p>
                    )}

                    <div className="kav-result__details">
                        <div className="kav-result__row">
                            <span>{isP2PRequest ? "Aanvraagnummer" : "Reserveringsnummer"}</span>
                            <strong>{reservationNumber}</strong>
                        </div>
                        <div className="kav-result__row">
                            <span>Voertuig</span>
                            <strong>{vehicleName || "--"}</strong>
                        </div>
                        <div className="kav-result__row">
                            <span>Kenteken</span>
                            <strong>{vehiclePlate}</strong>
                        </div>
                        <div className="kav-result__row">
                            <span>Start</span>
                            <strong>{formatDateTime(reservationFrom)}</strong>
                        </div>
                        <div className="kav-result__row">
                            <span>Einde</span>
                            <strong>{formatDateTime(reservationTo)}</strong>
                        </div>
                    </div>
                    <p>
U kunt op het tijdstip dat de reservering ingaat met het callcenter bellen dan helpen zij u verder. We openen de deuren voor u en vertellen u in het kort hoe het voertuig te gebruiken. Als u terug komt helpen wij de reservering te beëindigen. Daarna zorgen wij dat u de borg, verrekend met het openstaande bedrag voor de verhuur, snel teruggestort krijgt.
                    </p>

                    <div className="kav-callout" style={{marginTop: "16px"}}>
                        <span>Bel ons als u klaar bent te gaan rijden op nummer </span>
                        <a href="tel:0880883434" style={{color: "red", fontWeight: 700}}>
                            088 - 088 34 34
                        </a>
                    </div>

                    {shouldShowAppDownload && (
                        <div className="kav-result__app">
                            <p> Denkt u vaker een busje bij ons te huren? Dan is de app efficiënter. </p>
                            {
                                clientDevice.isMobile ? (
                                    <div className="kav-result__storeActions">
                                        {
                                            clientDevice.platform === "ios" && (
                                                <a
                                                    className="kav-step-actions__btn kav-step-actions__btn--primary"
                                                    href={IOS_APP_URL}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Open in App Store
                                                </a>
                                            )
                                        }
                                        {
                                            clientDevice.platform === "android" && (
                                                <a
                                                    className="kav-step-actions__btn kav-step-actions__btn--primary"
                                                    href={ANDROID_APP_URL}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Open in Google Play
                                                </a>
                                            )
                                        }
                                        {
                                            clientDevice.platform === "mobile_other" && (
                                                <>
                                                    <a
                                                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                                                        href={IOS_APP_URL}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        App Store
                                                    </a>
                                                    <a
                                                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                                        href={ANDROID_APP_URL}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Google Play
                                                    </a>
                                                </>
                                            )
                                        }
                                    </div>
                                ) : (
                                    <div className="kav-result__desktop-app">
                                        <img src={APP_QR_URL} alt="Download KAV2GO app QR"/>
                                        <p>Scan de QR-code om de app direct op je telefoon te openen.</p>
                                    </div>
                                )
                            }
                        </div>
                    )}

                    {isPrivateShareP2PRequest && (
                        <div className="kav-result__app kav-result__app--privateshare">
                            <h2>Download de PrivateShare app</h2>
                            <p>Volg je aanvraag en rond je huur af in de app.</p>
                            {clientDevice.isMobile ? (
                                <>
                                    <div className="kav-result__app-copy">
                                        <p>
                                            Voor een optimaal gebruiksgemak hebben we de PrivateShare app ontwikkeld. Hiermee beheert u de reserveringen, doet u betalingen, communiceert de huurder met de eigenaar en is verhuur zonder sleuteloverdracht mogelijk.
                                        </p>
                                        <p>
                                            Gebruik de app om uw voertuig in te brengen of om een voertuig te reserveren om te huren.
                                        </p>
                                    </div>
                                    <div className="kav-result__storeActions">
                                        <a
                                            className="kav-step-actions__btn kav-step-actions__btn--primary"
                                            href={PRIVATESHARE_ANDROID_APP_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Open in Google Play
                                        </a>
                                    </div>
                                </>
                            ) : (
                                <div className="kav-result__desktop-app kav-result__desktop-app--privateshare">
                                    <div className="kav-result__qr">
                                        <img src={PRIVATESHARE_ANDROID_APP_QR_URL} alt="Download PrivateShare app QR"/>
                                        <p>Scan de QR-code om de app direct op je telefoon te openen.</p>
                                    </div>
                                    <div className="kav-result__app-copy">
                                        <p>
                                            Voor een optimaal gebruiksgemak hebben we de PrivateShare app ontwikkeld. Hiermee beheert u de reserveringen, doet u betalingen, communiceert de huurder met de eigenaar en is verhuur zonder sleuteloverdracht mogelijk.
                                        </p>
                                        <p>
                                            Gebruik de app om uw voertuig in te brengen of om een voertuig te reserveren om te huren.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}


                    <div className="kav-result__actions">
                        <button
                            type="button"
                            className="kav-step-actions__btn kav-step-actions__btn--ghost"
                            onClick={() => navigate("/")}
                        >
                            Naar homepage
                        </button>
                        {/*<Link*/}
                        {/*    to="/"*/}
                        {/*    className="kav-step-actions__btn kav-step-actions__btn--primary"*/}
                        {/*>*/}
                        {/*    Nieuwe reservering*/}
                        {/*</Link>*/}
                    </div>
                </div>
            </div>

            <Footer/>
        </>
    );
};

export default ReservationResultPage;
